import React, { useState } from 'react';
import { ArrowLeftIcon, DoorOpenIcon } from 'lucide-react';
import { useDemo } from '../../contexts/DemoContext';
import { ScreenContainer } from '../story/ScreenContainer';
import { ScreenHeading } from '../story/ScreenHeading';
import { Button } from '../story/Button';
import { SceneIllustration } from '../art/SceneIllustration';
import { PauseArt } from '../art/PauseArt';
type PauseMode = 'none' | 'ripple' | 'still' | 'beat' | 'line';
const OPTIONS: {
  id: Exclude<PauseMode, 'none'>;
  label: string;
}[] = [{
  id: 'ripple',
  label: 'Watch a slow ripple'
}, {
  id: 'still',
  label: 'Use a still image'
}, {
  id: 'beat',
  label: 'Tap a steady beat'
}, {
  id: 'line',
  label: 'Read one line'
}];
export function PauseScreen() {
  const {
    state,
    dispatch
  } = useDemo();
  const [mode, setMode] = useState<PauseMode>('none');
  const [taps, setTaps] = useState(0);
  const reduced = state.accessPreferences.reducedMotion;
  const rippling = mode === 'ripple' && !reduced;
  return <ScreenContainer data-id="element-193" data-mp="21swn6kg0c3ms">
      <div className="grid gap-8 lg:grid-cols-[1fr_1.2fr] lg:items-start" data-id="element-194" data-mp="21segetvpy70m">
        <div data-id="element-195" data-mp="26t26ndc4ijts">
          <ScreenHeading eyebrow="Pause" data-id="element-196" data-mp="26ssq0gcyxp0r">You can stay here.</ScreenHeading>
          <p className="mt-4 font-display text-xl text-forest" data-id="element-197" data-mp="1st15dwry5ads">Nothing to finish. Nothing to prove.</p>
          <p className="mt-2 text-ink/80" data-id="element-198" data-mp="1wu366i1ihuhv">Your place in the story is saved while this page stays open.</p>

          <div className="mt-6 grid gap-2 sm:grid-cols-2" data-id="element-199" data-mp="2kjfgjl8ks8lr">
            {OPTIONS.map(option => <button key={option.id} type="button" aria-pressed={mode === option.id} onClick={() => setMode(option.id)} className={`min-h-[48px] rounded-xl border px-4 text-left font-bold transition-colors duration-150 ease-out hc-strong ${mode === option.id ? 'border-teal bg-teal text-cream' : 'border-line bg-paper text-forest hover:bg-sand'}`} data-id="element-200" data-mp="2pjjvv7xqyxae">
                {option.label}
              </button>)}
          </div>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row" data-id="element-201" data-mp="3i35wq55quabx">
            <Button onClick={() => dispatch({
            type: 'CLOSE_PAUSE'
          })} icon={<ArrowLeftIcon size={18} aria-hidden="true" data-id="element-203" data-mp="394anslbbem4x" />} data-id="element-202" data-mp="3e4evblu8w6j0">
              Return to story
            </Button>
            <Button variant="secondary" onClick={() => dispatch({
            type: 'LEAVE'
          })} icon={<DoorOpenIcon size={18} aria-hidden="true" data-id="element-205" data-mp="2z7frm92ikxtt" />} data-id="element-204" data-mp="346ikyw94tfly">
              End this visit
            </Button>
          </div>
        </div>

        <div className="flex flex-col gap-4" data-id="element-206" data-mp="2qe2qwky7krx6">
          {mode !== 'beat' && mode !== 'line' && <SceneIllustration quietTitle="Quiet space" description={rippling ? 'Acacia shade over still water. A slow ripple spreads and fades.' : 'Acacia shade over still water. Nothing is moving.'} data-id="element-207" data-mp="2lfhp4h3b606m">
              <PauseArt rippling={rippling} data-id="element-208" data-mp="21dmvxwz7caeq" />
            </SceneIllustration>}
          {mode === 'ripple' && reduced && <p className="text-sm text-ink/80" data-id="element-209" data-mp="26an0u4kbf749">Reduced motion is on, so the water stays still.</p>}
          {mode === 'beat' && <div className="flex flex-col items-center gap-4 rounded-2xl border border-line bg-paper p-8 hc-strong" data-id="element-210" data-mp="2gaduy54yrk3t">
              <p className="text-center" data-id="element-211" data-mp="1in5ggu3drbbb">Tap at any pace. There is no right rhythm and no count.</p>
              <div className="relative flex h-40 w-40 items-center justify-center" data-id="element-212" data-mp="1nl70picjlgod">
                {taps > 0 && <span key={taps} className="anim-tap absolute inset-0 rounded-full border-4 border-[#4E8C86]" aria-hidden="true" data-id="element-213" data-mp="1sjs05fjyzwbr" />}
                <button type="button" onClick={() => setTaps(value => value + 1)} className="relative h-32 w-32 rounded-full bg-teal font-bold text-cream transition-transform duration-100 ease-out active:scale-[0.97]" data-id="element-214" data-mp="zvvuvmgc4w3d">
                  Tap
                </button>
              </div>
            </div>}
          {mode === 'line' && <blockquote className="rounded-2xl border border-line bg-paper p-8 font-display text-2xl leading-snug text-forest hc-strong" data-id="element-215" data-mp="3gc0wbhc4kak1">
              A pause is not disappearing. It is checking if the ground is still here.
            </blockquote>}
        </div>
      </div>
    </ScreenContainer>;
}